import { QueryKeys } from "@/constants/Query";
import { useApi } from "@/hooks/useApi";
import { useApiMutation } from "@/hooks/useApiMutation";
import axiosInstance from "@/lib/axios";
import { type CodeableConcept, Surgery } from "@curai/js-fhir-tools";
import { useQueryClient } from "@tanstack/react-query";

interface AddSurgicalHistoryRequest {
  patientId: string;
  performedDate: string;
  procedureCode: Pick<CodeableConcept, "coding" | "text">;
}

export const useAddSurgicalHistory = ({
  patientId,
  onSuccess,
  onError,
}: {
  patientId: string;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) => {
  const queryClient = useQueryClient();
  return useApiMutation({
    mutationOptions: {
      mutationFn: (data: AddSurgicalHistoryRequest) =>
        axiosInstance.post("/charting/surgical-history/", data),
      mutationKey:
        QueryKeys.Patient.MedicalRecords.SurgicalHistory.CREATE(patientId),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey:
            QueryKeys.Patient.MedicalRecords.SurgicalHistory.DEFAULT(patientId),
        });
        onSuccess?.();
      },
      onError,
    },
  });
};

interface GetSurgicalHistoryResponse {
  surgicalHistory: Surgery[];
}

export const useGetSurgicalHistory = ({ patientId }: { patientId: string }) => {
  return useApi<GetSurgicalHistoryResponse>({
    queryOptions: {
      queryKey:
        QueryKeys.Patient.MedicalRecords.SurgicalHistory.DEFAULT(patientId),
      queryFn: async () => {
        const response = await axiosInstance.get(
          "/charting/surgical-history/",
          {
            params: { patientId },
          },
        );
        const data: GetSurgicalHistoryResponse = response?.data;
        data.surgicalHistory = data.surgicalHistory.map(
          (resource: object) => new Surgery(resource),
        );
        return data;
      },
      enabled: Boolean(patientId),
    },
  });
};

interface UpdateSurgicalHistoryRequest {
  performedDate: string;
  procedureCode: Pick<CodeableConcept, "coding" | "text">;
}

export const useUpdateSurgicalHistory = ({
  surgicalHistoryId,
  patientId,
  onSuccess,
}: {
  surgicalHistoryId: string;
  patientId: string;
  onSuccess?: () => void;
}) => {
  const queryClient = useQueryClient();
  return useApiMutation({
    mutationOptions: {
      mutationFn: (data: UpdateSurgicalHistoryRequest) =>
        axiosInstance.patch(
          `/charting/surgical-history/${surgicalHistoryId}`,
          data,
        ),
      mutationKey: QueryKeys.Patient.MedicalRecords.SurgicalHistory.UPDATE(
        patientId,
        surgicalHistoryId,
      ),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey:
            QueryKeys.Patient.MedicalRecords.SurgicalHistory.DEFAULT(patientId),
        });
        onSuccess?.();
      },
    },
  });
};

export const useVoidSurgicalHistory = ({
  surgicalHistoryId,
  patientId,
  onSuccess,
}: {
  surgicalHistoryId: string;
  patientId: string;
  onSuccess?: () => void;
}) => {
  const queryClient = useQueryClient();
  return useApiMutation<void, void, void>({
    mutationOptions: {
      mutationFn: () =>
        axiosInstance.delete(`/charting/surgical-history/${surgicalHistoryId}`),
      mutationKey: QueryKeys.Patient.MedicalRecords.SurgicalHistory.VOID(
        patientId,
        surgicalHistoryId,
      ),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey:
            QueryKeys.Patient.MedicalRecords.SurgicalHistory.DEFAULT(patientId),
        });
        onSuccess?.();
      },
    },
  });
};
